import React, { useState, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import HeaderPage from '../components/HeaderPage';
import NavBar from '../components/NavBar';

const ringSizeData = [
    { indian: 1, circumference: 41, diameter: 13.05 },
    { indian: 2, circumference: 42, diameter: 13.37 },
    { indian: 3, circumference: 43, diameter: 13.69 },
    { indian: 4, circumference: 44, diameter: 14.01 },
    { indian: 5, circumference: 45, diameter: 14.33 },
    { indian: 6, circumference: 46, diameter: 14.65 },
    { indian: 7, circumference: 47, diameter: 14.97 },
    { indian: 8, circumference: 48, diameter: 15.29 },
    { indian: 9, circumference: 49, diameter: 15.61 },
    { indian: 10, circumference: 50, diameter: 15.92 },
    { indian: 11, circumference: 51, diameter: 16.24 },
    { indian: 12, circumference: 52, diameter: 16.56 },
    { indian: 13, circumference: 53, diameter: 16.88 },
    { indian: 14, circumference: 54, diameter: 17.20 },
    { indian: 15, circumference: 55, diameter: 17.52 },
    { indian: 16, circumference: 56, diameter: 17.83 },
    { indian: 17, circumference: 57, diameter: 18.14 },
    { indian: 18, circumference: 58, diameter: 18.47 },
    { indian: 19, circumference: 59, diameter: 18.79 },
    { indian: 20, circumference: 60, diameter: 19.11 },
    { indian: 21, circumference: 61, diameter: 19.43 },
    { indian: 22, circumference: 62, diameter: 19.75 },
    { indian: 23, circumference: 63, diameter: 20.07 },
    { indian: 24, circumference: 64, diameter: 20.39 },
    { indian: 25, circumference: 65, diameter: 20.70 },
    { indian: 26, circumference: 66, diameter: 21.01 },
    { indian: 27, circumference: 67, diameter: 21.33 },
    { indian: 28, circumference: 68, diameter: 21.64 },
    { indian: 29, circumference: 69, diameter: 21.96 },
    { indian: 30, circumference: 70, diameter: 22.28 },
];

// Slider range: index 0–29, maps to ringSizeData
const MIN_IDX = 0;
const MAX_IDX = ringSizeData.length - 1;

// Base px diameter for Indian size 1 at 96 PPI. We scale visually.
// We'll render the circle at the actual mm diameter using a px/mm ratio.
// Typical screen: 1 CSS px ≈ 0.2646 mm  →  1 mm ≈ 3.78 px (96dpi)
const MM_TO_PX = 3.78;

const RingSizePage = () => {
    const navigate = useNavigate();
    const [sizeIdx, setSizeIdx] = useState(18); // default: Indian 19
    const tableRef = useRef(null);

    const current = ringSizeData[sizeIdx];
    const circlePx = current.diameter * MM_TO_PX;

    const handleSliderChange = (e) => {
        const idx = Number(e.target.value);
        setSizeIdx(idx);
        // Scroll table to highlight row
        setTimeout(() => {
            const row = document.getElementById(`ring-row-${idx}`);
            if (row) row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }, 50);
    };

    return (
        <>
            <Helmet>
                <title>Ring Size Checker</title>
                <style>{`
          .ring-size-page {
            font-family: 'Segoe UI', sans-serif;
            background: #f4faf5;
            min-height: 100vh;
          }

          /* ── Checker card ── */
          .ring-checker-card {
            background: #fff;
            border-radius: 16px;
            padding: 20px 16px 16px;
            margin: 12px 16px 0;
            box-shadow: 0 2px 12px rgba(60,160,90,0.08);
          }

          .ring-instruction {
            font-size: 13px;
            color: #555;
            line-height: 1.55;
            margin-bottom: 16px;
          }

          /* ── Circle canvas ── */
          .ring-canvas {
            position: relative;
            width: 100%;
            height: 180px;
            background: #f0faf2;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
          }

          .ring-canvas::before {
            content: '';
            position: absolute;
            inset: 0;
            background-image:
              linear-gradient(rgba(60,160,90,0.08) 1px, transparent 1px),
              linear-gradient(90deg, rgba(60,160,90,0.08) 1px, transparent 1px);
            background-size: 20px 20px;
          }

          .ring-circle {
            border-radius: 50%;
            border: 2px solid #3a9e5f;
            background: transparent;
            transition: width 0.15s ease, height 0.15s ease;
            position: relative;
            z-index: 1;
            flex-shrink: 0;
          }

          .ring-size-badge {
            position: absolute;
            top: 10px;
            right: 12px;
            background: #3a9e5f;
            color: #fff;
            border-radius: 20px;
            padding: 4px 12px;
            font-size: 13px;
            font-weight: 600;
            z-index: 2;
            letter-spacing: 0.3px;
          }

          /* ── Slider ── */
          .ring-slider-wrap {
            padding: 18px 4px 4px;
          }

          .ring-slider-labels {
            display: flex;
            justify-content: space-between;
            font-size: 11px;
            color: #aaa;
            margin-bottom: 6px;
          }

          input[type=range].ring-slider {
            -webkit-appearance: none;
            appearance: none;
            width: 100%;
            height: 4px;
            border-radius: 4px;
            background: linear-gradient(
              to right,
              #3a9e5f 0%,
              #3a9e5f calc(var(--pct) * 1%),
              #b8dfc2 calc(var(--pct) * 1%),
              #b8dfc2 100%
            );
            outline: none;
            cursor: pointer;
          }

          input[type=range].ring-slider::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 22px;
            height: 22px;
            border-radius: 50%;
            background: #3a9e5f;
            border: 3px solid #fff;
            box-shadow: 0 2px 6px rgba(40,150,80,0.35);
            cursor: pointer;
          }

          input[type=range].ring-slider::-moz-range-thumb {
            width: 22px;
            height: 22px;
            border-radius: 50%;
            background: #3a9e5f;
            border: 3px solid #fff;
            box-shadow: 0 2px 6px rgba(40,150,80,0.35);
            cursor: pointer;
          }

          /* ── Info pills ── */
          .ring-info-pills {
            display: flex;
            gap: 10px;
            margin: 14px 16px 0;
          }

          .ring-pill {
            flex: 1;
            background: #fff;
            border-radius: 12px;
            padding: 12px;
            text-align: center;
            box-shadow: 0 1px 8px rgba(60,160,90,0.08);
          }

          .ring-pill-label {
            font-size: 11px;
            color: #aaa;
            margin-bottom: 4px;
          }

          .ring-pill-value {
            font-size: 20px;
            font-weight: 700;
            color: #3a9e5f;
          }

          .ring-pill-unit {
            font-size: 11px;
            color: #aaa;
          }

          /* ── Table ── */
          .ring-table-section {
            margin: 16px 16px 0;
            background: #fff;
            border-radius: 16px;
            overflow: hidden;
            box-shadow: 0 2px 12px rgba(60,160,90,0.08);
          }

          .ring-table-header {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            background: #e8f5ec;
            padding: 10px 12px;
          }

          .ring-table-header span {
            font-size: 12px;
            font-weight: 600;
            color: #1a5c35;
            text-align: center;
          }

          .ring-table-body {
            max-height: 260px;
            overflow-y: auto;
          }

          .ring-table-body::-webkit-scrollbar {
            width: 4px;
          }
          .ring-table-body::-webkit-scrollbar-thumb {
            background: #b8dfc2;
            border-radius: 4px;
          }

          .ring-table-row {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            padding: 10px 12px;
            border-bottom: 1px solid #eaf5ec;
            transition: background 0.2s;
          }

          .ring-table-row span {
            font-size: 13px;
            color: #444;
            text-align: center;
          }

          .ring-table-row.active {
            background: #e8f7ec;
          }

          .ring-table-row.active span {
            color: #3a9e5f;
            font-weight: 700;
          }

          .ring-table-row:last-child {
            border-bottom: none;
          }

          /* ── Bottom padding for NavBar ── */
          .ring-bottom-pad {
            height: 80px;
          }
        `}</style>
            </Helmet>

            <HeaderPage title="Ring Size Checker" />

            <main className="page-content mypadding ring-size-page overflow-auto pb-4">

                {/* Checker card */}
                <div className="ring-checker-card">
                    <p className="ring-instruction">
                        Place your ring on the circle and adjust the size using the slider.
                        The circle should overlap with the inner edge of your ring to get a
                        near accurate measurement.
                    </p>

                    {/* Circle preview */}
                    <div className="ring-canvas">
                        <div
                            className="ring-circle"
                            style={{ width: circlePx, height: circlePx }}
                        />
                        <div className="ring-size-badge">Size {current.indian}</div>
                    </div>

                    {/* Slider */}
                    <div className="ring-slider-wrap">
                        <div className="ring-slider-labels">
                            <span>Size 1</span>
                            <span>Size 30</span>
                        </div>
                        <input
                            type="range"
                            className="ring-slider"
                            min={MIN_IDX}
                            max={MAX_IDX}
                            value={sizeIdx}
                            onChange={handleSliderChange}
                            style={{ '--pct': (sizeIdx / MAX_IDX) * 100 }}
                        />
                    </div>
                </div>

                {/* Info pills */}
                <div className="ring-info-pills">
                    <div className="ring-pill">
                        <div className="ring-pill-label">Indian Size</div>
                        <div className="ring-pill-value">{current.indian}</div>
                    </div>
                    <div className="ring-pill">
                        <div className="ring-pill-label">Circumference</div>
                        <div className="ring-pill-value">{current.circumference}</div>
                        <div className="ring-pill-unit">mm</div>
                    </div>
                    <div className="ring-pill">
                        <div className="ring-pill-label">Diameter</div>
                        <div className="ring-pill-value">{current.diameter.toFixed(2)}</div>
                        <div className="ring-pill-unit">mm</div>
                    </div>
                </div>

                {/* Reference table */}
                <div className="ring-table-section">
                    <div className="ring-table-header">
                        <span>Indian Size</span>
                        <span>Circumference (mm)</span>
                        <span>Diameter (mm)</span>
                    </div>
                    <div className="ring-table-body" ref={tableRef}>
                        {ringSizeData.map((row, idx) => (
                            <div
                                key={row.indian}
                                id={`ring-row-${idx}`}
                                className={`ring-table-row${idx === sizeIdx ? ' active' : ''}`}
                                onClick={() => setSizeIdx(idx)}
                                style={{ cursor: 'pointer' }}
                            >
                                <span>{row.indian}</span>
                                <span>{row.circumference}</span>
                                <span>{row.diameter.toFixed(2)}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="ring-bottom-pad" />
            </main>
        </>
    );
};

export default RingSizePage;